import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { dateService } from '../../domain/services/dateService';
import { SmartLink } from '../SmartLink';
import { useBoundingClientRect } from '../../hooks/useBoundingClientRect';
import { SponsoredImage } from '../SponsoredImage';
import { theme } from '../../domain/theme';

const sponsorBarContainer = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  align-content: center;
  min-height: 44px;
  text-align: center;

  & a {
    max-height: 42px;
  }

  & p {
    ${theme.font.family('boldWeb')};
    font-weight: 400;
    font-size: 16px;
    letter-spacing: 3px;
    line-height: 1.5;
    margin: 0;
    padding-right: 10px;
    color: #000;
    text-transform: uppercase;
  }
`;
const sponsorBar = css`
  visibility: visible;
  background-color: #f2f2f2;
  position: sticky;
  z-index: 295;
  width: 100%;
  top: 0;
  transform: translateY(0);

  &.black {
    background-color: #000;
  }

  &.black .sponsor_bar_container p {
    color: #fff;
  }
`;
export const SponsoredBar = ({ content, articleRef }) => {
  if (content.branded) {
    const barRef = useRef(null);
    const getArticleTopPositioning = () => {
      const articleElement = barRef.current.closest('article');
      return articleElement ? articleElement.getBoundingClientRect().top : 0;
    };
    const contextualNavigationHeight = 50;
    const isContextualNavigationAffectingStickiness = () => {
      const isContextualNavigationVisible = document.querySelector(
        '#main-navigation-nav[data-is-sticky=true]'
      );
      const articleTopPositioning = getArticleTopPositioning();
      return (
        isContextualNavigationVisible &&
        articleTopPositioning < contextualNavigationHeight
      );
    };

    const sponsoredText =
      content.sponsoredLogo?.entity?.mediaImage && content.sponsoredTextImage
        ? content.sponsoredTextImage
        : content.sponsoredText;

    const isWithinDateRange = dateService.isWithinDateRange(
      Math.floor(new Date().getTime() / 1000),
      content.sponsoredStartDate?.timestamp,
      content.sponsoredEndDate?.timestamp
    );

    if (sponsoredText && isWithinDateRange) {
      const [barFixed, setBarFixed] = useState(0);

      let { sponsoredBackgroundColor } = content;
      if (!sponsoredBackgroundColor) {
        sponsoredBackgroundColor = 'black';
      }
      const sponsoredContent = (
        <div className="sponsor_bar_container" css={sponsorBarContainer}>
          {content.sponsoredLogo?.entity?.mediaImage ? (
            <>
              <p>{sponsoredText}</p>
              <SponsoredImage content={content} />
            </>
          ) : (
            <>
              {content.sponsoredClickThrough?.url?.path ? (
                <SmartLink
                  to={content.sponsoredClickThrough.url.path}
                  rel="sponsored"
                >
                  <p>{sponsoredText}</p>
                </SmartLink>
              ) : (
                <p>{sponsoredText}</p>
              )}
            </>
          )}
        </div>
      );

      const { top: articleTopPosition } = useBoundingClientRect(articleRef, {
        debounceWait: 50
      });
      useEffect(() => {
        if (isContextualNavigationAffectingStickiness()) {
          if (
            articleTopPosition > 0 &&
            articleTopPosition < contextualNavigationHeight
          ) {
            setBarFixed(contextualNavigationHeight - articleTopPosition);
          } else {
            setBarFixed(contextualNavigationHeight);
          }
        } else {
          setBarFixed(0);
        }
      }, [articleTopPosition]);

      return (
        <div
          ref={barRef}
          style={{
            transition: 'transform .35s ease-in-out',
            transform: `translateY(${barFixed}px)`
          }}
          className={`sponsor_bar ${sponsoredBackgroundColor}`}
          css={sponsorBar}
        >
          {sponsoredContent}
        </div>
      );
    }
  }
  return <div />;
};

SponsoredBar.propTypes = {
  content: PropTypes.objectOf(PropTypes.any).isRequired,
  articleRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any })
  ]).isRequired
};
